import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Plus, Edit2, Trash2, Building, Search, RefreshCw, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

function DepartmentManagement({ user, onLogout }) {
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    department_type: ''
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/org-structure/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingDept) {
        await axios.put(`${API}/api/org-structure/departments/${editingDept.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Department updated successfully!');
      } else {
        await axios.post(`${API}/api/org-structure/departments`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Department created successfully!');
      }
      
      setShowForm(false);
      resetForm();
      fetchDepartments();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (dept) => {
    if (!window.confirm(`Are you sure you want to delete "${dept.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/org-structure/departments/${dept.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Department deleted successfully!');
      fetchDepartments();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({ name: '', code: '', description: '', department_type: '' });
    setEditingDept(null);
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name || '',
      code: dept.code || '',
      description: dept.description || '',
      department_type: dept.department_type || ''
    });
    setShowForm(true);
  };

  const filteredDepartments = departments.filter(dept =>
    dept.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dept.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dept.department_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by department type
  const groupedDepartments = filteredDepartments.reduce((acc, dept) => {
    const type = dept.department_type || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(dept);
    return acc;
  }, {});

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Department Management</h1>
            <p className="text-gray-600 mt-2">Manage organizational departments ({departments.length} total)</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchDepartments}
              disabled={dataLoading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
              style={{backgroundColor: '#2b3380'}}
            >
              <Plus className="w-5 h-5" />
              Add Department
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search departments by name, code, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {dataLoading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading departments...</p>
          </div>
        ) : (
          <>
            {/* Departments by Type */}
            {Object.entries(groupedDepartments).map(([type, depts]) => (
              <div key={type} className="mb-8">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{color: '#2b3380'}}>
                  <Building className="w-6 h-6" />
                  {type} ({depts.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {depts.map((dept) => (
                    <div key={dept.id} className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold" style={{color: '#2b3380'}}>{dept.name}</h3>
                          <p className="text-sm text-gray-500">Code: {dept.code}</p>
                        </div>
                        <div className="flex gap-1">
                          <button 
                            onClick={() => handleEdit(dept)} 
                            className="p-2 hover:bg-blue-50 rounded text-blue-600"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDelete(dept)} 
                            className="p-2 hover:bg-red-50 rounded text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {dept.description && (
                        <p className="text-sm text-gray-600">{dept.description}</p>
                      )}
                      <div className="mt-3 pt-3 border-t">
                        <span className={`text-xs px-2 py-1 rounded ${dept.active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {dept.active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {filteredDepartments.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Building className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500 mb-2">No departments found</p>
                <p className="text-sm text-gray-400">
                  {searchTerm ? 'Try a different search term' : 'Click "Add Department" to create one'}
                </p>
              </div>
            )}
          </>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {editingDept ? 'Edit Department' : 'Add Department'}
                  </h2>
                  <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Department Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Sales - B2B"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Department Code *</label>
                      <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., SLS-B2B"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Department Type</label>
                      <select
                        value={formData.department_type}
                        onChange={(e) => setFormData({...formData, department_type: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Type</option>
                        <option value="Sales">Sales</option>
                        <option value="Operations">Operations</option>
                        <option value="Support">Support</option>
                        <option value="Corporate">Corporate</option>
                        <option value="Technology">Technology</option>
                        <option value="Finance">Finance</option>
                        <option value="HR">HR</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Brief description of the department"
                      />
                    </div>

                    <div className="flex gap-2 pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 text-white rounded-lg"
                        style={{backgroundColor: loading ? '#cccccc' : '#2b3380'}}
                      >
                        {loading ? 'Saving...' : editingDept ? 'Update' : 'Create'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); resetForm(); }}
                        className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default DepartmentManagement;
